import React, { useEffect, useState } from 'react';
import useWalkme from 'src/hooks/useWalkme';
import { WalkmeAdobeTags, WalkmeName, WalkmeStep } from 'src/types/walkme.model';
import DashboardModal, { DashboardModalProps } from '../../molecules/dashboardModal/DashboardModal';
import { DashboardModalData } from 'src/types/dashboardModal.model';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { trackClick, trackImpression } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import { useUserAttributes } from 'src/hooks/queries/users';
import styles from './Walkme.module.scss';

export interface WalkmeDashboardModalWrapperProps {
    walkmeName: WalkmeName;
    stepIndex: number;
}

function WalkmeDashboardModalWrapper({ walkmeName, stepIndex }: WalkmeDashboardModalWrapperProps): JSX.Element {
    const { isEnabled, isNewUser, isLoading, incrementStepIndex, markAsDone, getStepData } = useWalkme(walkmeName);
    const [stepIndexMatches, setStepIndexMatches] = useState<boolean>(false);
    const [dashboardModalData, setDashboardModalData] = useState<DashboardModalData>(null);
    const { isMinWidthSmall } = useBreakpoints();
    const { pathname } = useLocation();
    const [adobeTagData, setAdobeTagData] = useState<WalkmeAdobeTags>(null);
    const [impressionCount, setImpressionCount] = useState<number>(0);
    const { data: userAttributesData = {} } = useUserAttributes();
    const { transactionClassificationStartupStep = 0 } = userAttributesData;

    const handleNext = async (): Promise<void> => {
        await incrementStepIndex(transactionClassificationStartupStep);
        if (adobeTagData) trackClick(pathname, adobeTagData.next, adobeTagData.widgetName);
    };

    const handleSkip = async (): Promise<void> => {
        await markAsDone();
        if (adobeTagData) trackClick(pathname, adobeTagData.skip, adobeTagData.widgetName);
    };

    const handleClose = async (): Promise<void> => {
        await markAsDone();
        if (adobeTagData) trackClick(pathname, adobeTagData.close, adobeTagData.widgetName);
    };

    useEffect(() => {
        setStepIndexMatches(transactionClassificationStartupStep === stepIndex);
        const newData: WalkmeStep = getStepData(transactionClassificationStartupStep);
        setDashboardModalData(newData?.data as DashboardModalData);
        setAdobeTagData(newData?.adobeTags || null);
    }, [walkmeName, stepIndex, isEnabled, isNewUser, isLoading, getStepData, transactionClassificationStartupStep]);

    if (isEnabled && !isNewUser && !isLoading && stepIndexMatches && dashboardModalData) {
        const props: DashboardModalProps = {
            id: `${walkmeName}-${stepIndex}`,
            ...dashboardModalData,
            handleNext,
            handleClose,
            handleSkip,
            isMinWidthSmall,
            className: styles.walkmeIn
        };
        if (adobeTagData && impressionCount === 0) {
            trackImpression(pathname, adobeTagData.impression, adobeTagData.widgetName);
            setImpressionCount(impressionCount + 1);
        }
        return <DashboardModal {...props} />;
    }
    return null;
}

export default WalkmeDashboardModalWrapper;
